import { useState } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Textarea,
  Spinner,
} from '@fluentui/react-components';
import type { TaxReturn } from '../types';

interface RequestDocumentsDialogProps {
  open: boolean;
  onClose: () => void;
  taxReturn: TaxReturn;
  onSend: (selectedDocuments: string[], customMessage: string) => Promise<void>;
}

// Common tax document types that can be requested
const commonDocumentTypes = [
  'W-2',
  '1099-MISC',
  '1099-NEC',
  '1099-INT',
  '1099-DIV',
  '1099-B',
  '1099-R',
  'K-1',
  '1098 (Mortgage Interest)',
  '1098-T (Tuition)',
  'Property Tax Statement',
  'Charitable Donation Receipts',
  'Medical Expense Records',
  'Business Expense Records',
  'Prior Year Tax Return',
  'Photo ID',
];

export function RequestDocumentsDialog({
  open,
  onClose,
  taxReturn,
  onSend,
}: RequestDocumentsDialogProps) {
  const [selectedDocs, setSelectedDocs] = useState<string[]>(
    taxReturn.missingDocuments?.length > 0 ? [...taxReturn.missingDocuments] : []
  );
  const [customMessage, setCustomMessage] = useState('');
  const [sending, setSending] = useState(false);

  // Combine Drake checklist items with common types
  const drakeItems = taxReturn.missingDocuments || [];
  const allItems = [...new Set([...drakeItems, ...commonDocumentTypes])];

  const handleToggle = (doc: string) => {
    setSelectedDocs(prev =>
      prev.includes(doc)
        ? prev.filter(d => d !== doc)
        : [...prev, doc]
    );
  };

  // What we will actually attempt, per channel that has an address to attempt it on.
  const email = taxReturn.clientEmail?.trim();
  const phone = taxReturn.clientPhone?.trim();
  const deliveryNote = !email && !phone
    ? 'No email address or mobile number is on file for this client, so there is nothing to send to.'
    : !phone
      ? `We'll email the list and an upload link to ${email}. No mobile number is on file, so no text will be sent.`
      : !email
        ? `We'll text the list and an upload link to ${phone}. No email address is on file, so no email will be sent.`
        : `We'll email the list and an upload link to ${email}, and text the same link to ${phone}.`;

  const handleSend = async () => {
    setSending(true);
    try {
      await onSend(selectedDocs, customMessage);
      onClose();
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => { if (!data.open) onClose(); }}>
      <DialogSurface style={{ maxWidth: '560px' }}>
        <DialogBody>
          <DialogTitle>Request Documents</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: '8px' }}>
                  Select documents to request from {taxReturn.clientName}:
                </div>
                <div style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid var(--tl-border)',
                  borderRadius: '4px',
                  padding: '8px',
                }}>
                  {allItems.map(doc => (
                    <Checkbox
                      key={doc}
                      label={doc}
                      checked={selectedDocs.includes(doc)}
                      onChange={() => handleToggle(doc)}
                      style={{
                        display: 'block',
                        padding: '4px 0',
                        fontWeight: drakeItems.includes(doc) ? 600 : 400,
                      }}
                    />
                  ))}
                </div>
                {drakeItems.length > 0 && (
                  <div style={{ fontSize: '12px', color: 'var(--tl-text-secondary)', marginTop: '4px' }}>
                    Bold items are from the Drake checklist.
                  </div>
                )}
              </div>
              <Textarea
                placeholder="Add a custom message for the client (optional)"
                value={customMessage}
                onChange={(_, data) => setCustomMessage(data.value)}
                rows={3}
              />
              {/*
                Says what will be attempted, on the channels that actually have somewhere to go.
                The previous copy promised "an email and SMS" unconditionally, including for
                clients with no mobile number on file. Part of the 2026-08-26 honesty pass — see
                RequestDocumentsDialog.test.tsx and utils/sentChannel.ts.
              */}
              <div
                data-testid="request-docs-delivery-note"
                style={{
                  fontSize: '13px',
                  color: 'var(--tl-text-secondary)',
                  padding: '8px',
                  backgroundColor: 'var(--tl-bg-subtle)',
                  borderRadius: '4px',
                }}
              >
                {deliveryNote}
              </div>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              onClick={handleSend}
              disabled={selectedDocs.length === 0 || sending}
            >
              {sending ? <Spinner size="tiny" /> : `Send Request (${selectedDocs.length} items)`}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
